import React from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import {
  SGridScrollableContainer,
  SGridCard,
  SGridContainer,
  SGridRow2,
  SGridRow3,
  LeftInputColumn,
  RightInputColumn,
  SGridRow4,
  SAddAssets,
  SAddIcon,
  SRemoveButton,
  SRemove,
  SGridRow,
  SCloseButton,
} from "./styles";
import { Header } from "./Header/Header";
import { Footer } from "./Footer/Footer";
import { PortfolioSummaryDonutChart } from "./PortfolioSummaryGraph/PortfolioSummaryGraph";
import { PortfolioName } from "./PortfolioName/PortfolioName";
import { PortfolioTotalAmount } from "./PortfolioTotalAmount/PortfolioTotalAmount";
import { PortfolioEditorAssetsList } from "./PortfolioEditorAssetsList/PortfolioEditorAssetsList";
import { PortfolioSummaryDonutChartLegend } from "./PortfolioSummaryGraphLegend/PortfolioSummaryGraphLegend";
import { BalancingButton } from "./BalancingButton/BalancingButton";
import { isSuperCarteiraTheme } from "../../../utils/domain/ThemeUtils";
import {
  useCurrentPortfolio,
  usePortfolioEditor,
  useSideEditor,
  useAllPortfolios,
  useBenchmarks,
  useCurrentPortfolioId,
  useCustomDateRange,
  useInitialApplication,
  usePeriod,
  useWallet,
  useWindowValue,
  useUserData,
  useSuperCarteiraFeature,
  UserDataType,
} from "../../../store";
import { useSaveUserData } from "../../../api";
import {
  SButton,
  SContentSaveIcon,
  SLinearProgress,
} from "../ShareButtons/styles";

export const PortfolioEditor: React.FC = () => {
  const { removeCurrentPortfolio, isLastPortfolio } = useCurrentPortfolio();
  const { setPortfolioBeingEdited } = useSideEditor();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    isExpanded,
    closeModal,
    isModalOpen,
    isSuperExpanded,
    setIsSuperExpanded,
  } = usePortfolioEditor();
  const { allPortfolios } = useAllPortfolios();
  const { selectedBenchmarks } = useBenchmarks();
  const { windowValue } = useWindowValue();
  const { selectedPeriod } = usePeriod();
  const { selectedCustomDateRange } = useCustomDateRange();
  const { initialApplication } = useInitialApplication();
  const [currentPortfolioId] = useCurrentPortfolioId();
  const { selectedCompetence, selectedConsolidated } = useWallet();
  const { userData } = useUserData();
  const { superCarteiraFeature } = useSuperCarteiraFeature();
  const { fetch: saveUserData, loading: isSavingUserData } = useSaveUserData();

  const isHomePage = location.pathname === "/";
  const isAnalysisPage = location.pathname === "/analysis";

  const canEditInAnalysisPage =
    isAnalysisPage && theme.portfolioEditor?.allowModalEditingOnAnalysisPage;
  const canEditInNormalPage = !isAnalysisPage && isSuperExpanded;

  const showCloseButton = canEditInAnalysisPage || canEditInNormalPage;

  const handleSuperExpand = (): void => {
    setIsSuperExpanded(false);
  };

  const handleAddAsset = (): void => {
    handleSuperExpand();
    navigate("../", { replace: true });
  };

  const showBalacingButton = theme?.portfolios?.balancingButton.show;

  const handleRemovePortfolio = (): void => {
    if (isLastPortfolio) {
      navigate("../");
    }
    setPortfolioBeingEdited(null);
    removeCurrentPortfolio();
  };

  const handleSavePortfolio = (): void => {
    const portfolioIdentifiers = Object.keys(allPortfolios)
      .map((portfolioKey) => allPortfolios[Number(portfolioKey)].identifier)
      .filter((identifier) => identifier !== undefined);

    const benchmarksIdentifiers = Object.keys(selectedBenchmarks)
      .map((benchmarkKey) => [
        selectedBenchmarks[benchmarkKey].asset.assetType,
        selectedBenchmarks[benchmarkKey].asset.identifier,
      ])
      .flat();

    const selectedCustomDateArray = [
      selectedCustomDateRange.initialDate,
      selectedCustomDateRange.finalDate,
    ];

    const newData = {
      id: portfolioIdentifiers[0],
      benchmark: benchmarksIdentifiers,
      period: selectedPeriod,
      windowValue,
      initialApplication,
      currentPortfolioId,
      selectedCustomDateRange:
        selectedPeriod === "DATA_PERSONALIZADA"
          ? selectedCustomDateArray
          : null,
      consolidated: selectedConsolidated,
      competence: selectedCompetence,
    } as unknown as UserDataType["portfolios"]["supercarteira"];

    saveUserData(userData.email, newData, superCarteiraFeature);
  };

  const handleCloseModal = (): void => {
    closeModal(isHomePage);
  };

  const PortfolioEditorContent = (): JSX.Element => (
    <>
      <Header />
      <SGridRow2 $isExpanded={isExpanded || isSuperExpanded}>
        <PortfolioSummaryDonutChart />
        <PortfolioSummaryDonutChartLegend />
      </SGridRow2>
      <SGridRow3>
        <SGridRow $isExpanded={isExpanded || isSuperExpanded}>
          <LeftInputColumn $isExpanded={isExpanded || isSuperExpanded}>
            <PortfolioName isHomePage={isHomePage} />
          </LeftInputColumn>
          <RightInputColumn $isExpanded={isExpanded || isSuperExpanded}>
            <PortfolioTotalAmount />
            {showBalacingButton ? <BalancingButton /> : null}
          </RightInputColumn>
        </SGridRow>
      </SGridRow3>
      {location.pathname === "/analysis" ||
      (location.pathname !== "/analysis" && isSuperExpanded) ? (
        <SGridRow4 $isModalEditorOpened={isModalOpen}>
          {!isModalOpen && !isSuperCarteiraTheme() ? (
            // NOTE: this is a polemic decision,
            //       devs should probably force this
            //       to appear while debugging
            <SRemoveButton onClick={handleRemovePortfolio}>
              <SRemove />
            </SRemoveButton>
          ) : null}
          <SAddAssets
            onClick={handleAddAsset}
            $isModalEditorOpened={isModalOpen}
          >
            <SAddIcon />
            {t("portfolioEditorAddAsset")}
          </SAddAssets>
        </SGridRow4>
      ) : null}
    </>
  );

  return (
    <>
      <SGridContainer $isExpanded={isExpanded}>
        <SGridCard $isExpanded={isExpanded}>
          {showCloseButton && (
            <SCloseButton
              onClick={() => {
                handleCloseModal();
                handleSuperExpand();
              }}
            >
              ×
            </SCloseButton>
          )}
          {theme.savePortfolioButton.show &&
            location.pathname === "/analysis" && (
              <SButton
                onClick={handleSavePortfolio}
                disabled={isSavingUserData}
              >
                <span style={{ height: "24px" }}>
                  <SContentSaveIcon />
                </span>
                <span style={{ width: "140px" }}>
                  {isSavingUserData ? (
                    <SLinearProgress variant="indeterminate" color="inherit" />
                  ) : (
                    t("share.save")
                  )}
                </span>
              </SButton>
            )}
          {isSuperCarteiraTheme() ? (
            <SGridScrollableContainer>
              <PortfolioEditorContent />
            </SGridScrollableContainer>
          ) : (
            <PortfolioEditorContent />
          )}
        </SGridCard>
        <PortfolioEditorAssetsList />
      </SGridContainer>
      {/* eslint-disable react/jsx-boolean-value */}
      <Footer onAnalysisClick={closeModal} />
    </>
  );
};
